import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Trophy, Crown, Medal, Plus } from "lucide-react";

const PODIUM = { 0: { bg: "#0A2540", color: "white", icon: Crown }, 1: { bg: "#0052CC", color: "white", icon: Medal }, 2: { bg: "#7C3AED", color: "white", icon: Medal } };

export default function Leaderboard() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/leaderboard?sort=points`).then(({ data }) => setRows(data)).finally(() => setLoading(false));
  }, []);

  const formatValue = (r) => r.points.toLocaleString();

  return (
    <div className="space-y-8" data-testid="leaderboard-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Competencia sana</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-[#0A2540] tracking-tighter mt-1">Ranking de proyectos.</h1>
        <p className="mt-2 text-sm text-[#5C6A7D] max-w-2xl">Top 10 proyectos de la empresa por puntos. Sube de posición reutilizando, compartiendo y recuperando sobrantes.</p>
      </div>

      {loading && <div className="text-sm text-[#5C6A7D]">Cargando…</div>}
      {!loading && rows.length === 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-16 text-center text-sm text-[#5C6A7D]">
          <Trophy className="mx-auto mb-3 text-[#5C6A7D]" size={28} strokeWidth={1.5} />
          Aún no hay proyectos en el ranking. Crea proyectos y comienza a ganar puntos.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <>
          {/* Top 3 podium */}
          <div className="grid sm:grid-cols-3 gap-4" data-testid="podium">
            {rows.slice(0, 3).map((r, i) => {
              const conf = PODIUM[i];
              const Icon = conf.icon;
              return (
                <Link key={r.project_id} to={`/projects/${r.project_id}/impact`} data-testid={`podium-rank-${i + 1}`}
                  className={`relative p-6 rounded-sm transition-all duration-200 hover:-translate-y-[2px] ${i === 0 ? "sm:order-2 sm:scale-[1.02]" : i === 1 ? "sm:order-1" : "sm:order-3"}`}
                  style={{ background: conf.bg, color: conf.color }}>
                  <div className="absolute top-4 right-4 opacity-30"><Icon size={48} strokeWidth={1.5} /></div>
                  <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">Posición #{i + 1}</div>
                  <div className="font-display font-extrabold text-2xl mt-1 leading-tight">{r.name}</div>
                  <div className="text-xs opacity-70 mt-1">{r.location || "—"}</div>
                  <div className="kpi-value text-5xl mt-4">{formatValue(r)}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] opacity-70 mt-1">Puntos</div>
                </Link>
              );
            })}
          </div>

          {/* Remaining list ordered by points */}
          {rows.length > 3 && (
            <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden" data-testid="leaderboard-list">
              <table className="w-full cams-table">
                <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
                  <tr>
                    <th className="px-4 py-3 text-left w-16">Pos.</th>
                    <th className="px-4 py-3 text-left">Proyecto</th>
                    <th className="px-4 py-3 text-right">Puntos</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(3).map((r, i) => (
                    <tr key={r.project_id} className="border-b border-[#E2E8F0] hover:bg-[#FAFAFA]/60" data-testid={`leaderboard-row-${i + 4}`}>
                      <td className="px-4 py-4 font-mono font-bold text-[#5C6A7D]">{i + 4}</td>
                      <td className="px-4 py-4">
                        <Link to={`/projects/${r.project_id}/impact`} className="font-semibold text-[#0A2540] hover:underline">{r.name}</Link>
                        <div className="text-xs text-[#5C6A7D]">{r.location || "—"}</div>
                      </td>
                      <td className="px-4 py-4 text-right font-display font-extrabold text-xl text-[#0A2540]">{formatValue(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Consolidated project-access: create a new project */}
      <button
        onClick={() => nav("/projects")}
        data-testid="create-project-fab"
        aria-label="Crear proyecto"
        className="fixed bottom-6 right-6 z-30 inline-flex items-center gap-2 rounded-full bg-[#0A2540] hover:bg-[#173B63] text-white px-5 h-14 shadow-lg shadow-[#0A2540]/25 transition-colors"
      >
        <Plus size={20} strokeWidth={2.25} />
        <span className="font-semibold text-sm hidden sm:inline">Crear proyecto</span>
      </button>
    </div>
  );
}
